import clsx from 'clsx';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import Buttons from '@/components/Buttons/Buttons';
import HtmlContent from '@/components/HtmlContent';
import DotOutline from '@/components/Icons/DotOutline';
import Globe from '@/components/Icons/Globe';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import TooltipOld from '@/components/Tooltip';
import { ToastTypeVariants } from '@/context/ToastContext';
import * as format from '@/format';
import getTimezoneKey from '@/helpers/getTimezoneKey';
import { useToast } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import ConfirmationModal from '@/modals/ConfirmationModal';
import { type CartItem } from '@/providers/ShoppingCart';
import { rescheduleBooking } from '@/tsClient';
import { type Booking } from '@/typings/api-models';
import type * as ApiModels from '@/typings/api-models';

import s from './CustomerTimeSelector.module.scss';

type ThisPrice = Pick<ApiModels.Price, 'sessionDuration' | 'sessionInterval' | 'sessionBuffer'> &
  CartItem & {
    priceDescription?: {
      html?: string | null;
    } | null;
  };

type ThisAvailability = {
  blockDuration: number;
  schedulingBuffer: number;
  schedulingInterval: number;
  blocks: Array<{
    hasConflict: boolean;
    startAt: string;
  }>;
};

type Props = {
  price: ThisPrice;
  creatorTimezone: string | null;
  isLoading: boolean;
  availability?: ThisAvailability[];
  booking?: Pick<Booking, 'id' | 'startAt' | 'priceId'>;
  onFinish?: (price: CartItem) => Promise<void>;
  onDateTimeSelect?: (props: { price: CartItem; sessionDuration: number; sessionTime: string }) => Promise<void>;
  preselectedSessionTime?: string | null;
  showDayOfWeek?: boolean;
  selectedDate?: string | null;
  selectedTeamMemberPersonId?: string | null;
  hidePriceDescription?: boolean;
  isCreatorView?: boolean;
  showConflictingTimes?: boolean;
};

const CustomerTimeSelector: FC<Props> = ({
  availability,
  creatorTimezone,
  isLoading,
  price,
  booking,
  onFinish,
  onDateTimeSelect,
  preselectedSessionTime,
  showDayOfWeek = false,
  selectedDate,
  selectedTeamMemberPersonId,
  hidePriceDescription = false,
  isCreatorView = false,
  showConflictingTimes = false,
}) => {
  const addToast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [pendingConflictTime, setPendingConflictTime] = useState<{ time: string; hasConflict: boolean } | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const ctaRef = useRef<HTMLDivElement>(null);
  const didAutoSelectRef = useRef<string | null>(null);

  const { getData: rescheduleBookingFn } = useNoodleApi(rescheduleBooking);
  const availableTimes = useMemo(() => {
    const blocks = availability?.filter(
      (a) => a.blockDuration === price?.sessionDuration && a.schedulingInterval === price?.sessionInterval,
    );
    const times = (blocks || [])
      .map((b) => b.blocks.filter((t) => (showConflictingTimes ? true : t.hasConflict === false)))
      .reduce((acc, val) => {
        const arr = [...acc];
        const existingTimes = arr.map((b) => b.startAt);
        val.forEach((it) => {
          if (!existingTimes.includes(it.startAt)) {
            arr.push(it);
          }
        });
        return arr;
      }, [])
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    return times;
  }, [price, availability, showConflictingTimes]);

  useEffect(() => {
    if (!preselectedSessionTime || selectedTime || !onDateTimeSelect || !price?.sessionDuration) {
      return;
    }

    if (didAutoSelectRef.current === preselectedSessionTime) {
      return;
    }

    const preselectedStartAtTime = Date.parse(preselectedSessionTime);
    if (Number.isNaN(preselectedStartAtTime)) {
      return;
    }

    const preselectedTime = availableTimes.find(
      (timeOption) => Date.parse(timeOption.startAt) === preselectedStartAtTime,
    );
    if (!preselectedTime) {
      return;
    }

    didAutoSelectRef.current = preselectedSessionTime;
    setSelectedTime(preselectedSessionTime);
    void onDateTimeSelect({
      price,
      sessionDuration: price.sessionDuration,
      sessionTime: preselectedSessionTime,
    });
  }, [availableTimes, onDateTimeSelect, preselectedSessionTime, price, selectedTime]);

  const handleBookingClick = async (): Promise<void> => {
    setIsSubmitting(true);
    if (price && selectedTime) {
      if (booking) {
        const { error } = await rescheduleBookingFn({
          bookingId: booking.id,
          productId: price.product.id,
          // Need to send the time in the creators TZ, so sending the time without the "Z"/timezone, the BE adjusts this based on the creators timezone
          startAt: selectedTime,
          teamMemberPersonId: selectedTeamMemberPersonId ?? undefined,
        });
        if (error) {
          setIsSubmitting(false);
          addToast(
            ToastTypeVariants.ERROR,
            `Failed to ${booking.startAt ? 're' : ''}schedule booking. Please contact support at support@glade.ai.`,
          );
        } else {
          addToast(ToastTypeVariants.SUCCESS, `Your booking has been ${booking.startAt ? 're' : ''}scheduled!`);
          onFinish?.(price);
        }
      }
    }
  };

  return (
    <div className={s.container}>
      {price?.priceDescription?.html && !hidePriceDescription && (
        <>
          <HtmlContent className={s.priceDescription} content={price?.priceDescription?.html} />
          <Spacer size='16px' />
        </>
      )}

      {showDayOfWeek && selectedDate ? (
        <div className={s.dayOfWeek}>
          <span className='body-md-bold'>{format.datetimeLocalTZ.dayOfWeek(`${selectedDate}T00:00:00`)}</span>
          <span className='caption' style={{ color: 'var(--color-gray-75)' }}>
            {format.datetimeLocalTZ.withoutTime(`${selectedDate}T00:00:00`)}
          </span>
          <Spacer size='20px' />
          <div className={s.timezone}>
            <Globe weight='fill' fill='var(--color-gray-75)' size={16} />
            <span className='caption' style={{ color: 'var(--color-gray-75)' }}>
              {creatorTimezone ? format.datetimeWithTZ.abbreviatedTimezone({ timezone: creatorTimezone }) : null}
            </span>
          </div>
          <Spacer size='16px' />
        </div>
      ) : (
        <>
          <span className='body-md-bold'>{onFinish || onDateTimeSelect ? 'Select Time' : 'Available Times'}</span>
          {creatorTimezone && <span className='body-sm'>{` (All times in ${getTimezoneKey(creatorTimezone)})`}</span>}
          <Spacer size='16px' />
        </>
      )}
      {isLoading ? (
        <ProgressIndicator isCentered />
      ) : (
        <>
          <div className={s.availableTimes} data-translate='false' data-testid='timeSelector__availableTimes'>
            {availableTimes.map((t) => (
              <TooltipOld
                key={t.startAt}
                text={t.hasConflict ? 'Selecting this time will conflict with another booking.' : null}
              >
                <Buttons
                  onClick={() => {
                    // If this time has a conflict and we're showing conflicting times, show confirmation modal
                    if (t.hasConflict && showConflictingTimes) {
                      setPendingConflictTime({ time: t.startAt, hasConflict: t.hasConflict });
                      setShowConfirmationModal(true);
                    } else {
                      // No conflict or not showing conflicting times, proceed normally
                      setSelectedTime(t.startAt);
                      if (onDateTimeSelect && price && price.sessionDuration) {
                        void onDateTimeSelect({
                          price,
                          sessionDuration: price?.sessionDuration,
                          sessionTime: t.startAt,
                        });
                      }
                      setTimeout(() => {
                        ctaRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 200);
                    }
                  }}
                  isSecondary
                  disabled={!onFinish && !onDateTimeSelect}
                  className={clsx(
                    t.startAt === selectedTime
                      ? [s.sessionButton, isCreatorView && s.customSessionButton]
                      : s.inactiveButton,
                  )}
                >
                  <span className='body-sm'>
                    {`${new Date(t.startAt).toLocaleString('en-US', {
                      hour: 'numeric',
                      hour12: true,
                      minute: 'numeric',
                    })}`}
                  </span>
                  {t.hasConflict && <DotOutline color='var(--color-warning)' weight='fill' className={s.warningIcon} />}
                </Buttons>
              </TooltipOld>
            ))}
          </div>
          <div ref={ctaRef}>
            {onFinish && (
              <>
                <Spacer size='24px' />
                <Buttons
                  isFetching={isSubmitting}
                  onClick={handleBookingClick}
                  disabled={!selectedTime}
                  isSecondary
                  className={s.sessionButton}
                >
                  <span className='body-md-bold'>
                    {booking ? <>{booking.startAt ? 'Reschedule' : 'Schedule'}</> : <>Schedule Booking</>}
                  </span>
                  <br />
                </Buttons>
              </>
            )}
          </div>
        </>
      )}
      {showConfirmationModal && pendingConflictTime && (
        <ConfirmationModal
          onClose={() => {
            setShowConfirmationModal(false);
            setPendingConflictTime(null);
          }}
          text='This time slot conflicts with an existing booking or blocked time. Are you sure you want to schedule at this time?'
          onSubmit={() => {
            // User confirmed, proceed with selecting this time
            setSelectedTime(pendingConflictTime.time);
            if (onDateTimeSelect && price && price.sessionDuration) {
              void onDateTimeSelect({
                price,
                sessionDuration: price?.sessionDuration,
                sessionTime: pendingConflictTime.time,
              });
            }
            setShowConfirmationModal(false);
            setPendingConflictTime(null);
            setTimeout(() => {
              ctaRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 200);
          }}
          cta='Schedule Anyway'
          variant='attention'
        />
      )}
    </div>
  );
};

export default CustomerTimeSelector;
